import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart3, Heart, Users, Image, FileText, Calendar, DollarSign,
  Star, TrendingUp, Activity, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isArtist = user?.user_type === 'independent_artist' ||
    user?.user_type === 'studio_artist' ||
    user?.user_type === 'studio' ||
    user?.user_type === 'artist';

  useEffect(() => {
    if (isArtist) {
      fetchAnalytics();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArtist]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.detail || 'Error loading analytics');
    } finally {
      setLoading(false);
    }
  };

  if (!isArtist) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <BarChart3 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
        <h2 className="font-['Cinzel'] text-2xl font-bold text-white mb-4">
          {language === 'es' ? 'Analytics solo para artistas' : 'Analytics for artists only'}
        </h2>
        <p className="text-zinc-500">
          {language === 'es'
            ? 'Esta sección está disponible solo para tatuadores y estudios.'
            : 'This section is only available for tattoo artists and studios.'}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-32 bg-zinc-900 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: language === 'es' ? 'Vistas de Perfil' : 'Profile Views',
      value: analytics?.total_views || 0,
      icon: Eye,
      color: 'text-blue-400'
    },
    {
      title: language === 'es' ? 'Seguidores' : 'Followers',
      value: analytics?.total_followers || 0,
      icon: Users,
      color: 'text-purple-400'
    },
    {
      title: language === 'es' ? 'Likes Totales' : 'Total Likes',
      value: analytics?.total_likes || 0,
      icon: Heart,
      color: 'text-rose-400'
    },
    {
      title: language === 'es' ? 'Trabajos' : 'Artworks',
      value: analytics?.total_artworks || 0,
      icon: Image,
      color: 'text-emerald-400'
    },
    {
      title: language === 'es' ? 'Cotizaciones' : 'Quotes',
      value: analytics?.total_quotes || 0,
      icon: FileText,
      color: 'text-yellow-400'
    },
    {
      title: language === 'es' ? 'Citas' : 'Appointments',
      value: analytics?.total_appointments || 0,
      icon: Calendar,
      color: 'text-cyan-400'
    },
    {
      title: language === 'es' ? 'Ingresos' : 'Revenue',
      value: `$${(analytics?.total_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'text-green-400'
    },
    {
      title: language === 'es' ? 'Rating Promedio' : 'Avg Rating',
      value: `${analytics?.average_rating || 0} ⭐`,
      icon: Star,
      color: 'text-yellow-400',
      subtitle: `${analytics?.reviews_count || 0} ${language === 'es' ? 'reseñas' : 'reviews'}`
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-testid="analytics-container">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="font-['Cinzel'] text-2xl sm:text-3xl font-bold text-white">
          {language === 'es' ? 'ANALYTICS' : 'ANALYTICS'}
        </h1>
        <p className="text-zinc-500 mt-1 text-sm sm:text-base">
          {language === 'es' ? 'Estadísticas de tu perfil y trabajos' : 'Your profile and work statistics'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <span className="text-xs text-zinc-500 uppercase tracking-wider">
                  {stat.title}
                </span>
              </div>
              <p className="text-2xl font-mono font-bold text-white">{stat.value}</p>
              {stat.subtitle && (
                <p className="text-xs text-zinc-500 mt-1">{stat.subtitle}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Analytics */}
      <Tabs defaultValue="artworks" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 w-full justify-start overflow-x-auto no-scrollbar">
          <TabsTrigger value="artworks" className="data-[state=active]:bg-zinc-800">
            <Heart size={16} className="mr-2" />
            {language === 'es' ? 'Top Trabajos' : 'Top Artworks'}
          </TabsTrigger>
          <TabsTrigger value="styles" className="data-[state=active]:bg-zinc-800">
            <TrendingUp size={16} className="mr-2" />
            {language === 'es' ? 'Estilos Populares' : 'Popular Styles'}
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-zinc-800">
            <Activity size={16} className="mr-2" />
            {language === 'es' ? 'Actividad Reciente' : 'Recent Activity'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="artworks" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="font-['Cinzel'] text-lg">
                {language === 'es' ? 'Trabajos con más likes' : 'Most liked artworks'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.likes_by_artwork?.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">
                  {language === 'es' ? 'Aún no tienes likes en tus trabajos' : 'No likes on your artworks yet'}
                </p>
              ) : (
                <div className="space-y-4">
                  {analytics?.likes_by_artwork?.map((artwork, index) => (
                    <div key={artwork.id} className="flex items-center gap-4">
                      <span className="text-2xl font-mono text-zinc-600 w-8">{index + 1}</span>
                      <div className="flex-1">
                        <p className="text-white font-medium">{artwork.title}</p>
                        <div className="w-full bg-zinc-800 h-2 mt-2 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-rose-500 to-rose-400"
                            style={{
                              width: `${Math.min(100, (artwork.likes / Math.max(...analytics.likes_by_artwork.map(a => a.likes))) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-rose-400">
                        <Heart size={16} fill="currentColor" />
                        <span className="font-mono">{artwork.likes}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="styles" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="font-['Cinzel'] text-lg">
                {language === 'es' ? 'Tus estilos más publicados' : 'Your most published styles'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.popular_styles?.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">
                  {language === 'es' ? 'Aún no tienes trabajos publicados' : 'No artworks published yet'}
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {analytics?.popular_styles?.map((style, index) => (
                    <div
                      key={style.style}
                      className="bg-zinc-800/50 border border-zinc-700 p-4 text-center"
                    >
                      <p className="text-3xl font-mono font-bold text-white mb-1">{style.count}</p>
                      <p className="text-sm text-zinc-400 uppercase tracking-wider">{style.style}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="font-['Cinzel'] text-lg">
                {language === 'es' ? 'Actividad reciente' : 'Recent activity'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.recent_activity?.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">
                  {language === 'es' ? 'Sin actividad reciente' : 'No recent activity'}
                </p>
              ) : (
                <div className="space-y-4">
                  {analytics?.recent_activity?.map((activity, index) => (
                    <div key={index} className="flex items-center gap-4 py-3 border-b border-zinc-800 last:border-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activity.type === 'like' ? 'bg-rose-900/30 text-rose-400' :
                        activity.type === 'quote' ? 'bg-yellow-900/30 text-yellow-400' :
                          'bg-cyan-900/30 text-cyan-400'
                        }`}>
                        {activity.type === 'like' && <Heart size={18} />}
                        {activity.type === 'quote' && <FileText size={18} />}
                        {activity.type === 'appointment' && <Calendar size={18} />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white">{activity.message}</p>
                        <p className="text-xs text-zinc-500">{activity.date}</p>
                      </div>
                      {activity.status && (
                        <span className={`text-xs uppercase tracking-wider px-2 py-1 ${activity.status === 'completed' || activity.status === 'deposit_paid' ? 'text-emerald-400 bg-emerald-900/30' :
                          activity.status === 'pending' ? 'text-yellow-400 bg-yellow-900/30' :
                            activity.status === 'responded' ? 'text-blue-400 bg-blue-900/30' :
                              'text-zinc-400 bg-zinc-800'
                          }`}>
                          {activity.status}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
